"use client";

import React, { useState } from "react";
import { X, Calendar, Building2, Stethoscope, ShieldCheck, CreditCard, Award } from "lucide-react";
import { InsurancePolicyLead } from "@/lib/demos/insuranceDb";
import { DIAGNOSTIC_LAB_PARTNERS, UNDERWRITING_DOCTORS } from "@/lib/demos/insuranceData";

interface InsuranceStageModalProps {
  policy: InsurancePolicyLead;
  targetStage: InsurancePolicyLead["stage"];
  onClose: () => void;
  onConfirm: (updates: Partial<InsurancePolicyLead> & { sendEmail: boolean }) => Promise<void>;
}

export default function InsuranceStageModal({
  policy,
  targetStage,
  onClose,
  onConfirm,
}: InsuranceStageModalProps) {
  const [submitting, setSubmitting] = useState(false);

  // Stage 3: Underwriting Review fields
  const [examDate, setExamDate] = useState(policy.medical_exam_date || "Tomorrow at 09:00 AM (Fasting Profile)");
  const [diagnosticCenter, setDiagnosticCenter] = useState(
    policy.diagnostic_center || DIAGNOSTIC_LAB_PARTNERS[0].name
  );
  const [underwritingDoctor, setUnderwritingDoctor] = useState(
    policy.underwriting_doctor || UNDERWRITING_DOCTORS[0].name
  );
  const [underwritingDecision, setUnderwritingDecision] = useState(
    policy.underwriting_decision || "Executive medical profile & TMT treadmill panel allocated"
  );

  // Stage 4: Premium Payment fields
  const [paymentReference, setPaymentReference] = useState(
    policy.payment_reference || `HDFC-NETBANK-${Math.floor(100000000 + Math.random() * 900000000)}`
  );
  const [settledPremium, setSettledPremium] = useState(policy.annual_premium);

  // Stage 5: Policy Issued fields
  const [policyNumber, setPolicyNumber] = useState(
    policy.policy_number || `POL-AEGIS-2026-${Math.floor(1000 + Math.random() * 9000)}`
  );
  const [taxSection, setTaxSection] = useState("Section 80C");

  const sym = policy.currency === "USD" ? "$" : "₹";

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const updates: any = {
      stage: targetStage,
      sendEmail: true,
    };

    if (targetStage === "Underwriting Review") {
      updates.medical_exam_date = examDate;
      updates.diagnostic_center = diagnosticCenter;
      updates.underwriting_doctor = underwritingDoctor;
      updates.underwriting_decision = underwritingDecision;
    } else if (targetStage === "Premium Payment Received") {
      updates.payment_reference = paymentReference;
      updates.annual_premium = settledPremium;
    } else if (targetStage === "Policy Issued (Won)") {
      updates.policy_number = policyNumber;
      updates.underwriting_decision = `Policy active. Approved under ${taxSection}.`;
    }

    try {
      await onConfirm(updates);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
      <div className="bg-slate-900 border border-blue-900/60 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl relative">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 bg-slate-950 border-b border-slate-800">
          <div>
            <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded bg-blue-950 text-blue-300 border border-blue-800">
              Stage Transition
            </span>
            <h3 className="text-sm font-bold text-white mt-1">
              Advance to: {targetStage}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Applicant Summary Banner */}
        <div className="p-4 bg-blue-950/30 border-b border-blue-900/40 flex items-center justify-between text-xs">
          <div>
            <div className="font-semibold text-white">{policy.prospect_name}</div>
            <div className="text-slate-400 text-[11px]">{policy.product_name}</div>
          </div>
          <div className="text-right">
            <div className="font-bold text-blue-400">{sym}{policy.coverage_amount.toLocaleString()}</div>
            <div className="text-slate-400 text-[11px]">{sym}{policy.annual_premium.toLocaleString()}/yr</div>
          </div>
        </div>

        <form onSubmit={handleSave} className="p-5 space-y-4 text-xs">
          
          {/* Target Stage 3: Underwriting Review */}
          {targetStage === "Underwriting Review" && (
            <>
              <div className="p-3 rounded-xl bg-blue-950/50 border border-blue-800/60 text-blue-200">
                📄 <strong>Automated PDF:</strong> Advancing to this stage will automatically generate and attach the <strong>Diagnostic Medical Exam &amp; Tele-MER Pass PDF</strong> to the applicant.
              </div>

              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Scheduled Medical Examination Slot
                </label>
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-950 border border-slate-800">
                  <Calendar className="w-4 h-4 text-blue-400 shrink-0" />
                  <input
                    type="text"
                    required
                    value={examDate}
                    onChange={(e) => setExamDate(e.target.value)}
                    className="w-full bg-transparent text-white focus:outline-none"
                    placeholder="e.g. Tomorrow at 09:00 AM (Fasting Profile)"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Allocated Diagnostic Lab Partner
                </label>
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-950 border border-slate-800">
                  <Building2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <select
                    value={diagnosticCenter}
                    onChange={(e) => setDiagnosticCenter(e.target.value)}
                    className="w-full bg-transparent text-white focus:outline-none"
                  >
                    {DIAGNOSTIC_LAB_PARTNERS.map((lab) => (
                      <option key={lab.name} value={lab.name} className="bg-slate-900">
                        {lab.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Assigned Medical Underwriting Officer
                </label>
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-950 border border-slate-800">
                  <Stethoscope className="w-4 h-4 text-indigo-400 shrink-0" />
                  <select
                    value={underwritingDoctor}
                    onChange={(e) => setUnderwritingDoctor(e.target.value)}
                    className="w-full bg-transparent text-white focus:outline-none"
                  >
                    {UNDERWRITING_DOCTORS.map((doc) => (
                      <option key={doc.name} value={doc.name} className="bg-slate-900">
                        {doc.name} ({doc.title})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Target Stage 4: Premium Payment Received */}
          {targetStage === "Premium Payment Received" && (
            <>
              <div className="p-3 rounded-xl bg-emerald-950/50 border border-emerald-800/60 text-emerald-200">
                💳 Confirm initial annual premium deposit settlement to clear policy for official issuance.
              </div>

              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Payment Reference / Bank UTR Number
                </label>
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-950 border border-slate-800">
                  <CreditCard className="w-4 h-4 text-emerald-400 shrink-0" />
                  <input
                    type="text"
                    required
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    className="w-full bg-transparent text-white focus:outline-none font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Settled Annual Premium Amount ({sym})
                </label>
                <input
                  type="number"
                  required
                  value={settledPremium}
                  onChange={(e) => setSettledPremium(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white font-bold focus:outline-none"
                />
              </div>
            </>
          )}

          {/* Target Stage 5: Policy Issued (Won) */}
          {targetStage === "Policy Issued (Won)" && (
            <>
              <div className="p-3 rounded-xl bg-emerald-950/50 border border-emerald-800/60 text-emerald-200">
                🛡️ <strong>Automated PDF:</strong> Advancing to this stage will automatically generate and attach the <strong>Certificate of Insurance &amp; Policy Schedule PDF</strong> with Section 80C/80D tax exemption endorsement!
              </div>

              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Official IRDAI Policy Number
                </label>
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-950 border border-slate-800">
                  <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                  <input
                    type="text"
                    required
                    value={policyNumber}
                    onChange={(e) => setPolicyNumber(e.target.value)}
                    className="w-full bg-transparent text-white focus:outline-none font-mono font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Statutory Tax Deduction Endorsement
                </label>
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-950 border border-slate-800">
                  <Award className="w-4 h-4 text-amber-400 shrink-0" />
                  <select
                    value={taxSection}
                    onChange={(e) => setTaxSection(e.target.value)}
                    className="w-full bg-transparent text-white focus:outline-none"
                  >
                    <option value="Section 80C" className="bg-slate-900">Section 80C (Life Insurance Exemption)</option>
                    <option value="Section 80D" className="bg-slate-900">Section 80D (Health Insurance Exemption)</option>
                    <option value="Section 10(10D)" className="bg-slate-900">Section 10(10D) (ULIP Maturity Exemption)</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Generic Stage Note */}
          {targetStage !== "Underwriting Review" &&
            targetStage !== "Premium Payment Received" &&
            targetStage !== "Policy Issued (Won)" && (
              <p className="text-slate-300">
                Confirm advancing this case to <strong>{targetStage}</strong>. An automated notification email will be dispatched to <strong>{policy.email}</strong>.
              </p>
            )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold shadow-lg shadow-blue-950 transition-all disabled:opacity-50"
            >
              {submitting ? "Processing Transition..." : "Confirm & Send Email"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
